import { Box, Stack } from '@mui/system';
import GroupGeneralInfoForm from './group-general-info-form';
import { useQuery } from '@tanstack/react-query';
import {
  getDirectoryGroupQuery,
  getDomainsQuery,
  getRoleOfGroupUserPortalQuery,
  groupSettingsQuery,
} from '../../../api/users-api';
import { useLocation, useParams } from 'react-router-dom';
import { useUserSettings } from '../../../utils/use-user-settings';
import { userPortalPagesConfig } from '../../../shared/user-portal-pages-config';

import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import SharedButton from '../../../shared/buttons/shared-button';
import SharedIconButton from '../../../shared/buttons/shared-icon-button';

export default function GroupGeneralInfo() {
  const { directoryRoles, changeLogRoles, primaryDomain, loggedInUser } =
    useUserSettings();
  const location = useLocation();
  const userView = location.pathname.includes(
    userPortalPagesConfig.userPortal.link
  );

  const { groupEmail } = useParams() as { groupEmail: string };

  const { isLoading, data: groupSettings } = useQuery(
    groupSettingsQuery(groupEmail, userView)
  );
  const { data: group, isFetching: isGroupFetching } = useQuery(
    getDirectoryGroupQuery(groupEmail, userView)
  );
  // Derive domain from the live group data (not the route param), so it
  // reflects a just-saved domain change rather than reverting to the
  // original URL's domain on every re-render.
  const domain = group ? group.email.split('@')[1] : groupEmail.split('@')[1];

  const { data: domains } = useQuery(getDomainsQuery(userView));

  const { data: groupRole } = useQuery(
    getRoleOfGroupUserPortalQuery(userView, loggedInUser, groupEmail)
  );

  const isInitialLoading = isLoading || isGroupFetching; // Only show skeleton on initial load

  if (group && groupSettings && !isInitialLoading) {
    return (
      <GroupGeneralInfoForm
        group={group}
        groupEmail={groupEmail}
        domain={domain}
        domains={domains || []}
        userView={userView}
        isUserManager={groupRole === 'MANAGER'}
        isUserOwner={groupRole === 'OWNER'}
        groupSettings={groupSettings}
        directoryRoles={directoryRoles}
        changeLogRoles={changeLogRoles}
        primaryDomain={primaryDomain}
      />
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        height: '100%',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{ position: 'absolute', top: -60, right: 0 }}
      >
        <SharedIconButton variant="refresh" tooltipLabel="Refresh" />
        <SharedButton variant="primary" disabled>
          Save
        </SharedButton>
      </Stack>
      <Stack gap={1} sx={{ margin: '8px 0' }}>
        <Skeleton variant="text" width={400} height={65} />
        <Stack flexDirection="row" alignItems="center" width="100%" gap={1}>
          <Skeleton variant="text" width={400} height={65} />
          <Typography sx={{ marginTop: '5px' }}>@{domain}</Typography>
        </Stack>
        <Skeleton variant="text" width={400} height={65} />
      </Stack>
    </Box>
  );
}
